import React, { useId, useRef } from 'react';
import { PaperClipOutlined, CloseOutlined } from '@ant-design/icons';
import { message as antMessage } from 'antd';
import {
  CHAT_PANEL_ATTACHMENT_ACCEPT,
  CHAT_PANEL_MAX_ATTACHMENTS,
  fileToPendingAttachment,
  formatAttachmentSize,
  isImageAttachment,
  type PendingChatAttachment,
} from './chatPanelAttachments';
import styles from './ChatPanel.module.css';

export interface ChatPanelAttachmentStripProps {
  attachments: PendingChatAttachment[];
  onChange: (next: PendingChatAttachment[]) => void;
  disabled?: boolean;
}

export const ChatPanelAttachmentStrip: React.FC<ChatPanelAttachmentStripProps> = ({
  attachments,
  onChange,
  disabled = false,
}) => {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFiles = async (fileList: FileList | null) => {
    if (!fileList?.length) return;

    const remaining = CHAT_PANEL_MAX_ATTACHMENTS - attachments.length;
    if (remaining <= 0) {
      void antMessage.warning(`Maximum ${CHAT_PANEL_MAX_ATTACHMENTS} attachments per message.`);
      return;
    }

    const files = Array.from(fileList).slice(0, remaining);
    const next = [...attachments];

    for (const file of files) {
      try {
        next.push(await fileToPendingAttachment(file));
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Could not attach file.';
        void antMessage.error(msg);
      }
    }

    onChange(next);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  return (
    <div className={styles.attachmentStrip}>
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        className={styles.attachmentInput}
        multiple
        accept={CHAT_PANEL_ATTACHMENT_ACCEPT}
        disabled={disabled || attachments.length >= CHAT_PANEL_MAX_ATTACHMENTS}
        onChange={(e) => void handleFiles(e.target.files)}
      />
      <label
        htmlFor={inputId}
        className={`${styles.attachBtn} ${disabled ? styles.attachBtnDisabled : ''}`}
        title="Add attachment"
        aria-label="Add attachment"
      >
        <PaperClipOutlined />
      </label>

      {attachments.length > 0 ? (
        <ul className={styles.attachmentList} aria-label="Message attachments">
          {attachments.map((item) => (
            <li key={item.id} className={styles.attachmentItem}>
              {item.previewUrl && isImageAttachment(item.mimeType) ? (
                <img src={item.previewUrl} alt="" className={styles.attachmentThumb} />
              ) : (
                <PaperClipOutlined className={styles.attachmentIcon} aria-hidden />
              )}
              <span className={styles.attachmentMeta}>
                <span className={styles.attachmentName}>{item.name}</span>
                <span className={styles.attachmentSize}>{formatAttachmentSize(item.size)}</span>
              </span>
              <button
                type="button"
                className={styles.attachmentRemove}
                aria-label={`Remove ${item.name}`}
                disabled={disabled}
                onClick={() => onChange(attachments.filter((a) => a.id !== item.id))}
              >
                <CloseOutlined />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};
